import { formGroup, formControl, Validators, FormDataType, formArray, FormGroupData, FormGroup, FormControl, FormArray } from '@core/utils/form';
import { UserId } from '@business/entities/account';
import { DealIntervalResponse } from '@business/entities/deal';
import { LabourTypeId } from '@business/entities/labour-type';
import { defineLabourForm } from '@modules/timeMaterials/AddEditTimeMaterialPage/TimeMaterialForm';
import { DiscountTypeId, TMFeeId, TMFeeType } from '@business/entities/tm';
import { ProposalDiscountCategory, ProposalDiscountType } from '@business/entities/proposal';

export type LaborItemsFormType = FormGroup<
  {
    labourTypeId: FormControl<string>;
    hour: FormControl<number>;
    cost: FormControl<number>;
    price: FormControl<number>;
  },
  FormGroupData<{
    labourTypeId: FormControl<string>;
    hour: FormControl<number>;
    cost: FormControl<number>;
    price: FormControl<number>;
  }>
>;

export type LineItemFormType = FormGroup<
  {
    service: FormControl<string>;
    description: FormControl<string>;
    title: FormControl<string>;
    qty: FormControl<number>;
    rate: FormControl<number>;
    classId: FormControl<string>;
    salesReps: FormControl<string[]>;
    taxable: FormControl<boolean>;
    isOptional: FormControl<boolean>;
    frequency: FormControl<DealIntervalResponse>;
    flexibleQty: FormControl<boolean>;
    selected: FormControl<boolean>;
    sectionId: FormControl<number | undefined>;
    labour: FormControl<number | undefined>;
    labourType: FormControl<string | undefined>;
    labourPrice: FormControl<number | undefined>;
    labourCost: FormControl<number | undefined>;
    labourItems: FormArray<LaborItemsFormType>;
    productLabourItems: FormArray<LaborItemsFormType>;
    cost: FormControl<number>;
    id: FormControl<number>;
    packageItems: FormArray<LineItemFormType>;
    productImage: FormControl<string | File | undefined>;
    isDiscount: FormControl<boolean>;
    isServicePhase: FormControl<boolean>;
    tierId: FormControl<number | string>;
    unitOfMeasure: FormControl<string>;
  },
  FormGroupData<{
    service: FormControl<string>;
    description: FormControl<string>;
    title: FormControl<string>;
    qty: FormControl<number>;
    rate: FormControl<number>;
    classId: FormControl<string>;
    salesReps: FormControl<string[]>;
    taxable: FormControl<boolean>;
    isOptional: FormControl<boolean>;
    frequency: FormControl<DealIntervalResponse>;
    flexibleQty: FormControl<boolean>;
    selected: FormControl<boolean>;
    sectionId: FormControl<number | undefined>;
    labour: FormControl<number | undefined>;
    labourType: FormControl<string | undefined>;
    labourPrice: FormControl<number | undefined>;
    labourCost: FormControl<number | undefined>;
    labourItems: FormArray<LaborItemsFormType>;
    productLabourItems: FormArray<LaborItemsFormType>;
    cost: FormControl<number>;
    id: FormControl<number>;
    packageItems: FormArray<LineItemFormType>;
    productImage: FormControl<string | File | undefined>;
    isDiscount: FormControl<boolean>;
    isServicePhase: FormControl<boolean>;
    tierId: FormControl<number | string>;
    unitOfMeasure: FormControl<string>;
  }>
>;

export const defineProposalLineItemForm = (classIdRequired?: boolean, isRequired = true): LineItemFormType => {
  return formGroup({
    packageItems: formArray(() => defineProposalLineItemForm(classIdRequired, isRequired)),
    service: formControl<string>({ placeholder: 'Product/Service/Package' }),
    description: formControl<string>({ required: isRequired, placeholder: 'Description' }),
    title: formControl<string>({ required: false, placeholder: 'Title' }),
    qty: formControl<number>({ placeholder: '0', initialValue: 1, required: isRequired, validators: [Validators.min(0)] }),
    rate: formControl<number>({ placeholder: '0.00', required: isRequired }),
    classId: formControl<string>({ placeholder: 'Select Class', required: classIdRequired }),
    salesReps: formControl<UserId[]>({}),
    taxable: formControl<boolean>({ initialValue: false }),
    isOptional: formControl<boolean>({ label: 'Optional Item', initialValue: false }),
    frequency: formControl<DealIntervalResponse>({ label: 'Interval', initialValue: DealIntervalResponse.onetime }),
    flexibleQty: formControl<boolean>({ label: 'Multiple quantities', initialValue: false }),
    selected: formControl<boolean>({ initialValue: true }),
    sectionId: formControl<number | undefined>(),
    labour: formControl<number | undefined>({ required: false }),
    labourType: formControl<LabourTypeId | undefined>({ required: false }),
    labourPrice: formControl<number | undefined>({ required: false }),
    labourCost: formControl<number | undefined>({ required: false }),
    labourItems: formArray(() => defineLabourForm()),
    productLabourItems: formArray(() => defineLabourForm()),
    cost: formControl<number>({ placeholder: '0.00', initialValue: 0 }),
    id: formControl<number>(),
    productImage: formControl<string | File | undefined>(),
    isDiscount: formControl<boolean>({ initialValue: false }),
    isServicePhase: formControl<boolean>({ initialValue: false }),
    tierId: formControl<number | string>({ placeholder: 'Tier' }),
    unitOfMeasure: formControl<string>(),
  });
};

export const defineProposalPackageLineItemForm = (classIdRequired?: boolean) => {
  return formGroup({
    service: formControl<string>({ placeholder: 'Product/Service' }),
    description: formControl<string>({ placeholder: 'Description' }),
    title: formControl<string>({ required: false, placeholder: 'Title' }),
    qty: formControl<number>({ placeholder: '0', initialValue: 1, required: true, validators: [Validators.min(0)] }),
    rate: formControl<number>({ placeholder: '0.00', required: true }),
    classId: formControl<string>({ placeholder: 'Select Class', required: classIdRequired }),
    salesReps: formControl<UserId[]>({}),
    taxable: formControl<boolean>({ initialValue: false }),
    isOptional: formControl<boolean>({ label: 'Optional Item', initialValue: false }),
    frequency: formControl<DealIntervalResponse>({ label: 'Interval', initialValue: DealIntervalResponse.onetime }),
    flexibleQty: formControl<boolean>({ label: 'Multiple quantities', initialValue: false }),
    selected: formControl<boolean>({ initialValue: true }),
    sectionId: formControl<number | undefined>(),
    labour: formControl<number | undefined>({ required: false }),
    labourType: formControl<LabourTypeId | undefined>({ required: false }),
    labourPrice: formControl<number | undefined>({ required: false }),
    labourCost: formControl<number | undefined>({ required: false }),
    labourItems: formArray(() => defineLabourForm()),
    productLabourItems: formArray(() => defineLabourForm()),
    cost: formControl<number>({ placeholder: '0.00', initialValue: 0 }),
    id: formControl<number>(),
  });
};

export type LineItemForm = ReturnType<typeof defineProposalLineItemForm>;
export type LineItemFormData = FormDataType<typeof defineProposalLineItemForm>;

export type LineItemPackageForm = ReturnType<typeof defineProposalPackageLineItemForm>;
export type LineItemPackageFormData = FormDataType<typeof defineProposalPackageLineItemForm>;

export const defineProposalPriceSectionForm = () => {
  return formGroup({
    id: formControl<number>({ required: false }),
    title: formControl<string>({ required: true, placeholder: 'Title' }),
    menuOrder: formControl<number>({ required: false, initialValue: 0 }),
    checked: formControl<boolean>({ required: false, initialValue: false }),
  });
};

export const defineTMFeeForm = () => {
  return formGroup({
    id: formControl<TMFeeId>({ required: true }),
    name: formControl<string>({ required: true, placeholder: 'Title' }),
    service: formControl<string>({ required: true, placeholder: 'Service' }),
    serviceId: formControl<string>({ required: true }),
    percent: formControl<number>({ required: true, initialValue: 0, validators: [Validators.min(-100), Validators.max(100)] }),
    isChecked: formControl<boolean>({ initialValue: true }),
    feeTotal: formControl<number>({ initialValue: 0 }),
    taxable: formControl<boolean>({ initialValue: false }),
    feeType: formControl<TMFeeType>({ placeholder: 'Apply Fee To' }),
  });
};

export type TmFeeForm = ReturnType<typeof defineTMFeeForm>;
export type TmFeeFormData = FormDataType<typeof defineTMFeeForm>;

export type PriceSectionForm = ReturnType<typeof defineProposalPriceSectionForm>;
export type PriceSectionFormData = FormDataType<typeof defineProposalPriceSectionForm>;

export function defineProposalDiscountForm() {
  return formGroup({
    isDiscount: formControl<boolean>({
      initialValue: false,
    }),
    type: formControl<ProposalDiscountType>({
      initialValue: ProposalDiscountType.percentage,
      placeholder: 'Discount Type',
    }),
    category: formControl<ProposalDiscountCategory>({
      initialValue: ProposalDiscountCategory.itemsWithLabor,
      placeholder: 'Discount Applies On',
    }),
    value: formControl<number>({
      placeholder: 'Value',
      initialValue: 0,
      validators: [Validators.min(0)],
    }),
    // Work Order discounts: which kind of discount it is (Settings > Discount Types) and a note, shown on invoices
    discountTypeId: formControl<DiscountTypeId | null>({
      initialValue: null,
      placeholder: 'Discount type',
    }),
    description: formControl<string>({
      initialValue: '',
      placeholder: 'Description',
    }),
  });
}

export type ProposalDiscountForm = ReturnType<typeof defineProposalDiscountForm>;
/** A saved discount as full form data (fills in the type and description that older discounts don't have). */
export function toProposalDiscountFormValue(discount?: Partial<ProposalDiscountFormData> | null): FormDataType<typeof defineProposalDiscountForm> {
  return {
    isDiscount: false,
    type: ProposalDiscountType.percentage,
    category: ProposalDiscountCategory.itemsWithLabor,
    value: 0,
    ...discount,
    discountTypeId: discount?.discountTypeId ?? null,
    description: discount?.description ?? '',
  };
}

// The type and description are optional: discounts saved before they existed don't have them
export type ProposalDiscountFormData = Omit<FormDataType<typeof defineProposalDiscountForm>, 'discountTypeId' | 'description'> & {
  discountTypeId?: DiscountTypeId | null;
  description?: string;
};
